// Sales Statistics interfaces for the frontend

export interface SalesStats {
  id: number;
  user_id: number;
  week_number?: number;
  year?: number;
  appointment_scheduled_plan?: number[];
  appointment_scheduled_actual?: number[];
  succesfulfirstmeetings_plan?: number[];
  succesfulfirstmeetings_actual?: number[];
  successfulassessments_plan?: number[];
  successfulassessments_actual?: number[];
  successfulproposalpresentations_plan?: number[];
  successfulproposalpresentations_actual?: number[];
  successfulclosures_plan?: number[];
  successfulclosures_actual?: number[];
  property_insurance_appointment_plan?: number[];
  property_insurance_appointment_actual?: number[];
  notes?: string[];
  created_at?: string;
}

export interface UpdatePlanRequest {
  user_id: number;
  week_number: number;
  year: number;
  appointment_scheduled_plan?: number[];
  succesfulfirstmeetings_plan?: number[];
  successfulassessments_plan?: number[];
  successfulproposalpresentations_plan?: number[];
  successfulclosures_plan?: number[];
  property_insurance_appointment_plan?: number[];
  day_indices?: number[];  // Tört hétnél: csak ezek a hét-pozíciók (0-4, h-p) kerülnek frissítésre
}

export interface UpdateActualRequest {
  user_id: number;
  week_number: number;
  year: number;
  day_index: number; // 0-4 (hétfő-péntek)
  appointment_scheduled_actual?: number;
  succesfulfirstmeetings_actual?: number;
  successfulassessments_actual?: number;
  successfulproposalpresentations_actual?: number;
  successfulclosures_actual?: number;
  property_insurance_appointment_actual?: number;
}

export interface UpdateNotesRequest {
  user_id: number;
  week_number: number;
  year: number;
  notes: (string | null)[];
  active_day_indices?: number[];  // Tört hétnél: csak ezek a pozíciók (0-4) tartoznak az aktuális hónaphoz
}

export interface SalesStatsResponse {
  success: boolean;
  data?: SalesStats;
  message?: string;
}

export interface SalesStatsUsersResponse {
  user_ids: number[];
}

// Sales metrics enum for consistency
export enum SalesMetricType {
  APPOINTMENT_SCHEDULED = 'appointmentscheduled',
  SUCCESSFUL_FIRST_MEETINGS = 'succesfulfirstmeetings',
  SUCCESSFUL_ASSESSMENTS = 'successfulassessments', 
  SUCCESSFUL_PROPOSAL_PRESENTATIONS = 'successfulproposalpresentations',
  SUCCESSFUL_CLOSURES = 'successfulclosures',
  PROPERTY_INSURANCE_APPOINTMENT = 'property_insurance_appointment'
}

// Sales statistic categories
export interface SalesStatCategories {
  name: string;
  displayName: string;
  planField: keyof SalesStats;
  actualField: keyof SalesStats;
  backgroundColor?: string;
  indentLevel?: number;
  isSubcategory?: boolean;
}

export const SALES_STAT_CATEGORIES: SalesStatCategories[] = [
  {
    name: 'appointmentscheduled',
    displayName: 'Egyeztetett időpont',
    planField: 'appointment_scheduled_plan',
    actualField: 'appointment_scheduled_actual'
  },
  {
    name: 'property_insurance_appointment',
    displayName: 'Vagyonra egyeztetett',
    planField: 'property_insurance_appointment_plan',
    actualField: 'property_insurance_appointment_actual'
  },
  {
    name: 'succesfulfirstmeetings',
    displayName: 'Sikeres első találkozó',
    planField: 'succesfulfirstmeetings_plan',
    actualField: 'succesfulfirstmeetings_actual'
  },
  {
    name: 'successfulassessments',
    displayName: 'Sikeres felmérés',
    planField: 'successfulassessments_plan',
    actualField: 'successfulassessments_actual'
  },
  {
    name: 'successfulproposalpresentations',
    displayName: 'Sikeres ajánlat prezentáció',
    planField: 'successfulproposalpresentations_plan',
    actualField: 'successfulproposalpresentations_actual'
  },
  {
    name: 'successfulclosures',
    displayName: 'Sikeres lezárás',
    planField: 'successfulclosures_plan',
    actualField: 'successfulclosures_actual'
  }
];

// Helper type for week data structure
export interface WeeklyData {
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
}

// Convert array to weekly data object
export function arrayToWeeklyData(arr: number[]): WeeklyData {
  return {
    monday: arr[0] || 0,
    tuesday: arr[1] || 0,
    wednesday: arr[2] || 0,
    thursday: arr[3] || 0,
    friday: arr[4] || 0
  };
}

// Convert weekly data object to array
export function weeklyDataToArray(data: WeeklyData): number[] {
  return [
    data.monday,
    data.tuesday,
    data.wednesday,
    data.thursday,
    data.friday
  ];
}

// Helper function to get week number and year from date
export function getWeekInfo(date: Date): { weekNumber: number; year: number } {
  const tempDate = new Date(date.getTime());
  tempDate.setHours(0, 0, 0, 0);
  tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
  const week1 = new Date(tempDate.getFullYear(), 0, 4);
  const weekNumber = 1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
  
  return {
    weekNumber,
    year: tempDate.getFullYear()
  };
}

// Get current week info
export function getCurrentWeekInfo(): { weekNumber: number; year: number } {
  return getWeekInfo(new Date());
}